import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./contexts/AuthContext";
import Header from "./components/Header";
import Home from "./pages/Home";
import Search from "./pages/Search";
import Login from "./pages/Login";
import Register from "./pages/Register";
import VisitorLogin from "./pages/VisitorLogin";
import AdminLogin from "./pages/AdminLogin";
import VisitorRegister from "./pages/VisitorRegister";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import CheckEmail from "./pages/CheckEmail";
import Contact from "./pages/Contact";
import MyRequests from "./pages/MyRequests";
import TicketDetail from "./pages/TicketDetail";
import AdminSupport from "./pages/AdminSupport";
import CommerceLogin from "./pages/CommerceLogin";
import CommerceRegister from "./pages/CommerceRegister";
import CommerceDetail from "./pages/CommerceDetail";
import MyTicket from "./pages/MyTicket";
import MerchantDashboard from "./pages/MerchantDashboard";
import PublicDisplay from "./pages/PublicDisplay";
import PaymentSuccess from "./pages/PaymentSuccess";
import Legal from "./pages/Legal";
import MyTickets from "./pages/MyTickets";
import AdminCommerces from "./pages/AdminCommerces";
import AdminTickets from "./pages/AdminTickets";
import Settings from "./pages/Settings";
import MyCards from "./pages/MyCards";
import Profile from "./pages/Profile";
import MerchantProfile from "./pages/MerchantProfile";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminOverview from "./pages/admin/AdminOverview";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminCommercesManage from "./pages/admin/AdminCommercesManage";
import AdminQueues from "./pages/admin/AdminQueues";
import AdminPayments from "./pages/admin/AdminPayments";
import AdminMaintenance from "./pages/admin/AdminMaintenance";
import AdminMonetization from "./pages/admin/AdminMonetization";
import AdminGuides from "./pages/admin/AdminGuides";
import HelpVisitor from "./pages/HelpVisitor";
import HelpEstablishment from "./pages/HelpEstablishment";
import HelpFAQ from "./pages/HelpFAQ";
import PublicQueueLanding from "./pages/PublicQueueLanding";
import GlobalQueueLanding from "./pages/GlobalQueueLanding";
import TVCodeEntry from "./pages/TVCodeEntry";
import MaintenanceGate from "./components/MaintenanceGate";
import ScheduledMaintenanceBanner from "./components/ScheduledMaintenanceBanner";
import RequireVerified from "./components/RequireVerified";
import PinnedTicketsLayer from "./components/PinnedTicketsLayer";
import CookieConsentBanner from "./components/CookieConsentBanner";
import FloatingHelpButton from "./components/FloatingHelpButton";
import OnboardingGuideBanner from "./components/OnboardingGuideBanner";
import HelpRouteGate from "./components/HelpRouteGate";
import FooterHelpLink from "./components/FooterHelpLink";

function Layout({ children }) {
  const loc = useLocation();
  // When the app is reached on the dedicated TV subdomain (tv.bypassgo.fr),
  // automatically open the 4-digit code entry page — even if the visitor
  // typed only the root URL on their TV. Layout chrome (header/footer) is
  // also hidden in that case.
  const isTVSubdomain = typeof window !== "undefined" && /^tv\./i.test(window.location.host);
  if (isTVSubdomain && loc.pathname !== "/tv" && !loc.pathname.startsWith("/display/")) {
    return <Navigate to="/tv" replace />;
  }
  const fullscreen = loc.pathname.startsWith("/display/") || loc.pathname === "/tv" || loc.pathname === "/admin/login";
  return (
    <MaintenanceGate>
      {!fullscreen && <ScheduledMaintenanceBanner />}
      {!fullscreen && <Header />}
      <main>{children}</main>
      {!fullscreen && <Footer />}
      {!fullscreen && <PinnedTicketsLayer />}
      {!fullscreen && <CookieConsentBanner />}
      {!fullscreen && <FloatingHelpButton />}
      {!fullscreen && <OnboardingGuideBanner />}
    </MaintenanceGate>
  );
}

function Footer() {
  return (
    <footer className="border-t border-border mt-4 md:mt-12 py-3 md:py-8 text-xs md:text-sm text-subtle">
      <div className="container mx-auto px-4 md:px-8 flex flex-col md:flex-row gap-2 md:gap-8 items-start md:items-center justify-between">
        <div className="flex items-center gap-2">
          <img src="/logo-bypassgo-b-only.png" alt="BypassGo" style={{ height: 20, width: "auto" }} className="object-contain md:!h-6" />
          <span className="font-display font-bold">
            <span style={{ color: "#1D2D7B" }}>Bypass</span><span style={{ color: "#2BB54A" }}>Go</span>
          </span>
          <span className="hidden sm:inline">· Gagnez du temps, avancez !</span>
        </div>
        <div className="flex gap-3 md:gap-6 flex-wrap">
          <a href="/contact" className="hover:text-ink">Contact</a>
          <FooterHelpLink />
          <a href="/legal#mentions-legales" className="hover:text-ink">Mentions légales</a>
          <a href="/legal#cgu" className="hover:text-ink">CGU</a>
          <a href="/legal#cgv" className="hover:text-ink">CGV</a>
          <a href="/legal#confidentialite" className="hover:text-ink">Confidentialité</a>
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent("bypassgo:open-cookie-banner"))}
            className="hover:text-ink underline-offset-2 hover:underline text-left"
            data-testid="footer-manage-cookies-btn"
          >
            Gérer mes cookies
          </button>
        </div>
      </div>
    </footer>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-center" richColors />
          <Layout>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/search" element={<Search />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/visitor/login" element={<VisitorLogin />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/visitor/register" element={<VisitorRegister />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/check-email" element={<CheckEmail />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/my-requests" element={<MyRequests />} />
              <Route path="/my-requests/:id" element={<TicketDetail admin={false} />} />
              <Route path="/admin/support" element={<AdminSupport />} />
              <Route path="/admin/support/:id" element={<TicketDetail admin={true} />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminOverview />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="commerces-manage" element={<AdminCommercesManage />} />
                <Route path="queues" element={<AdminQueues />} />
                <Route path="payments" element={<AdminPayments />} />
                <Route path="monetization" element={<AdminMonetization />} />
                <Route path="maintenance" element={<AdminMaintenance />} />
                <Route path="guides" element={<AdminGuides />} />
              </Route>
              <Route path="/commerce/login" element={<CommerceLogin />} />
              <Route path="/commerce/register" element={<CommerceRegister />} />
              <Route path="/commerce/:id" element={<CommerceDetail />} />
              <Route path="/q/global/:commerceId" element={<GlobalQueueLanding />} />
              <Route path="/q/:queueId" element={<PublicQueueLanding />} />
              <Route path="/my-ticket" element={<RequireVerified><MyTicket /></RequireVerified>} />
              <Route path="/merchant/dashboard" element={<RequireVerified><MerchantDashboard /></RequireVerified>} />
              <Route path="/display/:id" element={<PublicDisplay />} />
              <Route path="/tv" element={<TVCodeEntry />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/legal" element={<Legal />} />
              <Route path="/my-tickets" element={<RequireVerified><MyTickets /></RequireVerified>} />
              <Route path="/settings" element={<RequireVerified><Settings /></RequireVerified>} />
              <Route path="/admin/commerces" element={<AdminCommerces />} />
              <Route path="/admin/tickets" element={<AdminTickets />} />
              <Route path="/my-cards" element={<MyCards />} />
              <Route path="/profile" element={<RequireVerified><Profile /></RequireVerified>} />
              <Route path="/merchant/profile" element={<RequireVerified><MerchantProfile /></RequireVerified>} />
              {/* === BypassGo Help Center (additif) === */}
              <Route path="/help" element={<HelpRouteGate><HelpFAQ /></HelpRouteGate>} />
              <Route path="/help/faq" element={<HelpRouteGate><HelpFAQ /></HelpRouteGate>} />
              <Route path="/help/visitor" element={<HelpRouteGate><HelpVisitor /></HelpRouteGate>} />
              <Route path="/help/establishment" element={<HelpRouteGate><HelpEstablishment /></HelpRouteGate>} />
            </Routes>
          </Layout>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
